import { CommonModule } from '@angular/common';
import { Component, DestroyRef, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { ConfirmationService } from 'primeng/api';
import { Button } from 'primeng/button';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { PaginatorState } from 'primeng/paginator';
import { Select } from 'primeng/select';

import {
    PrestadorCorpoSocietarioCargoResponse,
    PrestadorCorpoSocietarioComposicaoFiltro,
    PrestadorCorpoSocietarioSaidaEmLoteRequest,
    PrestadorCorpoSocietarioSocioAtualizarRequest,
    PrestadorCorpoSocietarioSocioResponse,
    PrestadorCorpoSocietarioSocioSalvarRequest,
    PrestadorCorpoSocietarioSort
} from '@/core/api/sicom-api/registro/prestador/prestador-corpo-societario.model';
import { PrestadorCorpoSocietarioService } from '@/core/api/sicom-api/registro/prestador/prestador-corpo-societario.service';
import { PessoaDomain } from '@/core/api/sicom-api/registro/pessoa/pessoa.domain';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { CardContent } from '@/shared/components/card-content/card-content';
import { CustomTableComponent, TableMetadata, TablePerfilValue } from '@/shared/components/layout/custom-table/custom-table.component';
import { FotoMedicoCacheService } from '@/shared/components/foto-diretor/foto-medico-cache.service';
import { TabContainer, TabContainerOptionType, TabContent } from '@/shared/components/layout/tab-container/tab-container';
import { PesquisarPessoaCustomComponent } from '@/shared/components/pessoa/pesquisar-pessoa-custom/pesquisar-pessoa-custom';
import { SeletorMedicoCorpoClinico } from '@/shared/components/pessoa/seletor-medico-corpo-clinico/seletor-medico-corpo-clinico';
import { DateUtils } from '@/shared/utils/date-utils';
import { PERMISSOES } from '@/core/permissions/permissoes.catalog';
import { PermissionService } from '@/core/permissions/permission.service';
import { AppMenuItem } from '@/core/permissions/permission.model';
import { BotaoAcoes, EInconPos } from '@/shared/components/layout/botao-acoes/botao-acoes';
import { acaoMenuItem } from '@/shared/components/layout/botao-acoes/acao-menu-item';
import { CorpoSocietarioFinanceiroNavigationService } from './corpo-societario-financeiro-navigation.service';
import { PrestadorAberturaNavigationService } from '@/features/registro/prestadores/dashboard-prestadores/components/prestador-abertura-navigation.service';

import { DialogoPadraoDirective } from '@/shared/components/layout/dialogo-padrao/dialogo-padrao.directive';
type TableTag = {
    value: string;
    color?: string;
    textColor?: string;
};

type SocioTabelaRow = {
    id: string;
    selecao: boolean;
    alerta: TableTag;
    tipo: string;
    documento: string;
    ufSocio: string;
    nome: TablePerfilValue;
    cargo: string;
    dataEntrada: string;
    dataSaida: string;
    email: string;
    telefone: string;
    source: PrestadorCorpoSocietarioSocioResponse;
};

@Component({
    selector: 'app-corpo-societario-prestador',
    standalone: true,
    imports: [
        DialogoPadraoDirective,
        CommonModule,
        ReactiveFormsModule,
        BotaoAcoes,
        Button,
        CardContent,
        ConfirmDialog,
        CustomTableComponent,
        Dialog,
        InputText,
        PesquisarPessoaCustomComponent,
        SeletorMedicoCorpoClinico,
        Select,
        TabContainer,
        TabContent
    ],
    providers: [ConfirmationService],
    templateUrl: './corpo-societario-prestador.html',
    styleUrl: './corpo-societario-prestador.scss'
})
export class CorpoSocietarioPrestador {
    pessoa = input<Pessoa>();

    protected readonly roleCorpoSocietarioEditar = PERMISSOES.prestadores.corpoSocietario.editar;
    private readonly permissionService = inject(PermissionService);
    private readonly fotos = inject(FotoMedicoCacheService);

    readonly carregando = signal(false);
    readonly socios = signal<PrestadorCorpoSocietarioSocioResponse[]>([]);
    readonly sociosTabela = computed<SocioTabelaRow[]>(() => this.socios().map((socio) => this.toTabelaRow(socio)));
    readonly totalRecords = signal(0);
    readonly first = signal(0);
    readonly rows = signal(10);
    readonly cargos = signal<PrestadorCorpoSocietarioCargoResponse[]>([]);
    readonly dialogVisivel = signal(false);
    readonly pesquisaSocioVisivel = signal(false);
    readonly corpoClinicoVisivel = signal(false);
    readonly saidaEmLoteVisivel = signal(false);
    readonly salvando = signal(false);
    readonly salvandoSaidaEmLote = signal(false);
    readonly socioEmEdicao = signal<PrestadorCorpoSocietarioSocioResponse | null>(null);
    readonly selecionados = signal<Record<string, PrestadorCorpoSocietarioSocioResponse>>({});
    readonly selecionadosLista = computed(() => Object.values(this.selecionados()));
    readonly selecionadosCount = computed(() => this.selecionadosLista().length);

    selected: PrestadorCorpoSocietarioComposicaoFiltro = 'ATUAL';
    sortBy: PrestadorCorpoSocietarioSort = 'NOME';
    direction: 'ASC' | 'DESC' = 'ASC';

    protected readonly EInconPos = EInconPos;

    get itensAcoes(): AppMenuItem[] {
        return [
            acaoMenuItem({
                id: 'saida-lote',
                label: 'Saída em lote',
                icon: 'pi pi-calendar-times',
                cor: 'neutro',
                disabled: this.selected !== 'ATUAL' || this.selecionadosCount() < 2,
                permission: this.roleCorpoSocietarioEditar,
                command: () => this.abrirSaidaEmLote()
            }),
            acaoMenuItem({ id: 'novo', label: 'Novo', icon: 'pi pi-plus', cor: 'novo', permission: this.roleCorpoSocietarioEditar, command: () => this.novoSocio() })
        ];
    }

    readonly formSocio = inject(FormBuilder).nonNullable.group({
        idSocio: [0, [Validators.required, Validators.min(1)]],
        ufSocio: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(2)]],
        nome: ['', [Validators.required]],
        tipoSocioCodigo: [null as number | null],
        idCargo: [null as number | null, [Validators.required]],
        dataEntrada: ['', [Validators.required]],
        dataSaida: ['']
    });
    readonly formSaidaEmLote = inject(FormBuilder).nonNullable.group({
        dataSaida: ['', [Validators.required]]
    });

    readonly options: TabContainerOptionType[] = [
        { label: 'Composição Atual', value: 'ATUAL' },
        { label: 'Composição Antiga', value: 'ANTIGA' }
    ];
    // `tipo` não aparece aqui: virou subtítulo de `documento`. Coluna listada duas vezes sairia
    // duas vezes — a própria e a linha secundária.
    readonly columnOrder = ['selecao', 'alerta', 'documento', 'ufSocio', 'nome', 'cargo', 'dataEntrada', 'dataSaida', 'email', 'telefone'];
    readonly tableMetadata: TableMetadata = {
        headers: {
            selecao: {
                type: 'selection',
                default: '-',
                hidden: false,
                value: 'Seleção',
                command: (item: SocioTabelaRow) => this.toggleSelecionado(item.source),
                // Sem a role de escrita, o checkbox fica desabilitado (não escondido, para manter a
                // coluna consistente com o restante do grid): sem seleção, "Saída em lote" nunca habilita.
                disabled: (item: SocioTabelaRow) => !this.podeSelecionar(item.source) || !this.permissionService.can(this.roleCorpoSocietarioEditar, { uf: this.pessoa()?.uf })
            },
            alerta: { type: 'tag', default: '-', hidden: false, value: 'Status' },
            // Documento e tipo são o mesmo assunto — "quem é este sócio" — e liam-se juntos mesmo
            // separados. Numa coluna só, o documento identifica e o tipo qualifica logo abaixo; o
            // nome do agrupamento no cabeçalho segue a convenção do Acompanhamento
            // ("Comissão / CT / GT e Local"). A ordenação por tipo continua no `orderBy`, agora
            // pendurada na coluna agrupada — ver o `sortMap` em `toSortBy`.
            documento: {
                type: 'text',
                default: '-',
                hidden: false,
                value: 'Documento / Tipo',
                orderBy: { asc: true },
                sub: { key: 'tipo', type: 'text', value: 'Tipo', default: '-' }
            },
            ufSocio: { type: 'text', default: '-', hidden: false, value: 'UF', orderBy: { asc: true } },
            // Retrato + nome. Sócio pessoa jurídica não tem foto no cadastro de biometria, então cai
            // nas iniciais — o que é correto: o nome ali é de empresa, e a busca por CRM não acha
            // nada. Sem foto e sem nome, a coluna não desenha avatar nenhum.
            nome: { type: 'perfil', default: '-', hidden: false, value: 'Nome', orderBy: { asc: true } },
            cargo: { type: 'text', default: '-', hidden: false, value: 'Cargo', orderBy: { asc: true } },
            dataEntrada: { type: 'text', default: '-', hidden: false, value: 'Entrada', orderBy: { asc: true } },
            dataSaida: { type: 'text', default: '-', hidden: false, value: 'Saída', orderBy: { asc: true } },
            // E-mail e telefone comiam quase um terço da largura repetindo o mesmo valor linha
            // após linha — e-mail chegava a cortar o Nome e o Cargo para fora da tela. Recolhidos
            // em pictograma, o dado continua na linha (tooltip) e no card expandido, por extenso.
            // Saída fica como coluna: na aba "Composição Antiga" ela é o dado que a aba existe
            // para mostrar.
            email: { type: 'text', default: '-', hidden: false, value: 'E-mail', orderBy: { asc: true }, resumo: { icon: 'pi pi-envelope' } },
            telefone: { type: 'text', default: '-', hidden: false, value: 'Telefone', resumo: { icon: 'pi pi-phone' } }
        },
        title: 'nome',
        actions: {
            financeiro: {
                label: 'Informações financeiras',
                icon: 'pi pi-dollar',
                color: 'primary',
                command: (item: SocioTabelaRow) => this.abrirInformacoesFinanceiras(item.source)
            },
            abrirPrestador: {
                label: 'Abrir prestador',
                icon: 'pi pi-external-link',
                color: 'primary',
                // Só faz sentido para sócio pessoa jurídica prestador (tipoCodigo 5); nos demais fica desabilitado.
                command: (item: SocioTabelaRow) => this.abrirPrestador(item.source),
                disabled: (item: SocioTabelaRow) => item.source.tipoCodigo !== 5
            },
            editar: {
                label: 'Editar',
                icon: 'pi pi-pencil',
                color: 'secondary',
                command: (item: SocioTabelaRow) => this.editarSocio(item.source),
                permission: PERMISSOES.prestadores.corpoSocietario.editar
            },
            remover: {
                label: 'Remover',
                icon: 'pi pi-trash',
                color: 'danger',
                command: (item: SocioTabelaRow) => this.confirmarRemocao(item.source),
                permission: PERMISSOES.prestadores.corpoSocietario.editar
            }
        },
        defaultSortField: 'nome',
        defaultSortOrder: 1
    };

    private readonly destroyRef = inject(DestroyRef);
    private readonly service = inject(PrestadorCorpoSocietarioService);
    private readonly financeiroNavigation = inject(CorpoSocietarioFinanceiroNavigationService);
    private readonly aberturaNavigation = inject(PrestadorAberturaNavigationService);
    private readonly confirmation = inject(ConfirmationService);

    constructor() {
        effect(() => {
            const pessoa = this.pessoa();
            if (!pessoa?.id || !pessoa.uf) {
                this.limpar();
                return;
            }

            untracked(() => this.carregar());
        });
        this.carregarCargos();
    }

    onComposicaoAlterada(event: { value: string }): void {
        this.selected = event.value as PrestadorCorpoSocietarioComposicaoFiltro;
        this.first.set(0);
        this.limparSelecionados();
        this.carregar();
    }

    onPaginaAlterada(event: PaginatorState): void {
        this.first.set(event.first ?? 0);
        this.rows.set(event.rows ?? 10);
        this.limparSelecionados();
        this.carregar();
    }

    onOrdenacaoAlterada(event: { field?: string; order?: number }): void {
        const sortBy = this.toSortBy(event.field);
        if (!sortBy) {
            return;
        }
        this.sortBy = sortBy;
        this.direction = event.order === -1 ? 'DESC' : 'ASC';
        this.first.set(0);
        this.limparSelecionados();
        this.carregar();
    }

    novoSocio(): void {
        this.socioEmEdicao.set(null);
        this.formSocio.reset({
            idSocio: 0,
            ufSocio: '',
            nome: '',
            tipoSocioCodigo: null,
            idCargo: null,
            dataEntrada: new Date().toISOString().substring(0, 10),
            dataSaida: ''
        });
        this.dialogVisivel.set(true);
    }

    editarSocio(socio: PrestadorCorpoSocietarioSocioResponse): void {
        this.socioEmEdicao.set(socio);
        this.formSocio.reset({
            idSocio: socio.idSocio,
            ufSocio: socio.ufSocio,
            nome: socio.nome ?? '',
            tipoSocioCodigo: socio.tipoCodigo ?? null,
            idCargo: socio.idCargo ?? null,
            dataEntrada: socio.dataEntrada ?? '',
            dataSaida: socio.dataSaida ?? ''
        });
        this.dialogVisivel.set(true);
    }

    abrirPesquisaSocio(): void {
        this.pesquisaSocioVisivel.set(true);
    }

    selecionarSocio(pessoa: PessoaDomain): void {
        this.formSocio.patchValue({
            idSocio: Number(pessoa.id),
            ufSocio: pessoa.uf,
            nome: pessoa.nome ?? '',
            tipoSocioCodigo: this.tipoSocioCodigo(pessoa.tipo)
        });
    }

    salvarSocio(): void {
        const pessoa = this.pessoa();
        if (!pessoa?.id || !pessoa.uf || this.formSocio.invalid || !this.validarDatasFormulario()) {
            this.formSocio.markAllAsTouched();
            return;
        }

        const request = this.requestSalvar(Number(pessoa.id), pessoa.uf);
        const socioOriginal = this.socioEmEdicao();
        const requisicao = socioOriginal ? this.service.atualizar(this.requestAtualizar(request, socioOriginal)) : this.service.criar(request);

        this.salvando.set(true);
        requisicao
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.salvando.set(false))
            )
            .subscribe(() => {
                this.dialogVisivel.set(false);
                this.carregar();
            });
    }

    confirmarRemocao(socio: PrestadorCorpoSocietarioSocioResponse): void {
        this.confirmation.confirm({
            message: `Excluir ${socio.nome ?? 'sócio'} do corpo societário?`,
            accept: () => this.removerSocio(socio)
        });
    }

    abrirInformacoesFinanceiras(socio: PrestadorCorpoSocietarioSocioResponse): void {
        this.financeiroNavigation.abrirSocio(this.pessoa() as PessoaDomain | undefined, socio);
    }

    abrirPrestador(socio: PrestadorCorpoSocietarioSocioResponse): void {
        if (socio.tipoCodigo !== 5) {
            return;
        }
        this.aberturaNavigation.abrirPrestador(this.pessoa() as PessoaDomain | undefined, { id: socio.idSocio, uf: socio.ufSocio, crm: socio.crm ?? undefined, nome: socio.nome ?? undefined, tipo: 'PRESTADOR' }, '/prestadores/corpo-societario');
    }

    abrirSaidaEmLote(): void {
        this.formSaidaEmLote.reset({ dataSaida: new Date().toISOString().substring(0, 10) });
        this.saidaEmLoteVisivel.set(true);
    }

    confirmarSaidaEmLote(): void {
        const pessoa = this.pessoa();
        if (!pessoa?.id || !pessoa.uf || this.formSaidaEmLote.invalid || this.saidaEmLoteDataInvalida()) {
            this.formSaidaEmLote.markAllAsTouched();
            return;
        }

        const request = this.requestSaidaEmLote(Number(pessoa.id), pessoa.uf);
        this.salvandoSaidaEmLote.set(true);
        this.service
            .informarSaidaEmLote(request)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.salvandoSaidaEmLote.set(false))
            )
            .subscribe(() => {
                this.saidaEmLoteVisivel.set(false);
                this.limparSelecionados();
                this.carregar();
            });
    }

    toggleSelecionado(socio: PrestadorCorpoSocietarioSocioResponse): void {
        if (!this.podeSelecionar(socio)) {
            return;
        }
        const key = this.socioKey(socio);
        const selecionados = { ...this.selecionados() };
        if (selecionados[key]) {
            delete selecionados[key];
        } else {
            selecionados[key] = socio;
        }
        this.selecionados.set(selecionados);
    }

    datasInvalidas(): boolean {
        const { dataEntrada, dataSaida } = this.formSocio.getRawValue();
        return !!dataEntrada && !!dataSaida && dataSaida < dataEntrada;
    }

    saidaEmLoteDataInvalida(): boolean {
        const dataSaida = this.formSaidaEmLote.controls.dataSaida.value;
        return !!dataSaida && this.selecionadosLista().some((socio) => !!socio.dataEntrada && dataSaida < socio.dataEntrada);
    }

    private carregar(): void {
        const pessoa = this.pessoa();
        if (!pessoa?.id || !pessoa.uf) {
            this.limpar();
            return;
        }

        this.carregando.set(true);
        this.service
            .listar({
                id: Number(pessoa.id),
                uf: pessoa.uf,
                composicao: this.selected,
                sortBy: this.sortBy,
                direction: this.direction,
                page: Math.floor(this.first() / this.rows()),
                size: this.rows()
            })
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => this.carregando.set(false))
            )
            .subscribe({
                next: (response) => {
                    const socios = response.content ?? [];
                    this.socios.set(socios);
                    // Dispara as fotos da página; as linhas são `computed` sobre o cache, então cada
                    // retrato que chega refaz a sua linha sozinho.
                    this.fotos.precarregar(socios.map((socio) => ({ crm: socio.crm, uf: socio.ufSocio })));
                    this.totalRecords.set(response.totalElements ?? 0);
                },
                // Sem isto, a falha na troca de prestador deixaria os dados do anterior na tela.
                error: () => this.limpar()
            });
    }

    private carregarCargos(): void {
        this.service
            .listarCargos()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((cargos) => this.cargos.set(cargos ?? []));
    }

    private removerSocio(socio: PrestadorCorpoSocietarioSocioResponse): void {
        this.service
            .remover({
                idPrestador: socio.idPrestador,
                ufPrestador: socio.ufPrestador,
                idSocio: socio.idSocio,
                ufSocio: socio.ufSocio,
                idCargo: socio.idCargo!,
                dataEntrada: socio.dataEntrada!
            })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.carregar());
    }

    private limpar(): void {
        this.socios.set([]);
        this.totalRecords.set(0);
        this.first.set(0);
        this.limparSelecionados();
    }

    private toTabelaRow(socio: PrestadorCorpoSocietarioSocioResponse): SocioTabelaRow {
        return {
            id: this.socioKey(socio),
            selecao: this.estaSelecionado(socio),
            alerta: this.alerta(socio),
            tipo: socio.tipo ?? '-',
            documento: socio.documento ?? '-',
            ufSocio: socio.ufSocio ?? '-',
            nome: this.fotos.perfilMedico(socio.nome, socio.crm, socio.ufSocio),
            cargo: socio.cargo ?? '-',
            dataEntrada: this.formatarData(socio.dataEntrada),
            dataSaida: this.formatarData(socio.dataSaida),
            email: socio.email ?? '-',
            telefone: socio.telefone ?? '-',
            source: socio
        };
    }

    private estaSelecionado(socio: PrestadorCorpoSocietarioSocioResponse): boolean {
        return !!this.selecionados()[this.socioKey(socio)];
    }

    private podeSelecionar(socio: PrestadorCorpoSocietarioSocioResponse): boolean {
        return this.selected === 'ATUAL' && !!socio.idCargo && !!socio.dataEntrada;
    }

    private alerta(socio: PrestadorCorpoSocietarioSocioResponse): TableTag {
        if (socio.falecido && socio.devedor) {
            return { value: 'Falecido / Devedor', color: '#7c3aed', textColor: '#ffffff' };
        }
        if (socio.falecido) {
            return { value: 'Falecido', color: '#a78bfa', textColor: '#ffffff' };
        }
        if (socio.devedor) {
            return { value: 'Devedor', color: '#ef4444', textColor: '#ffffff' };
        }
        return { value: 'Regular', color: '#10b981', textColor: '#ffffff' };
    }

    private toSortBy(field?: string): PrestadorCorpoSocietarioSort | null {
        const sortMap: Record<string, PrestadorCorpoSocietarioSort> = {
            // A coluna agrupada ordena por TIPO, que é o critério que o backend expõe — não há
            // ordenação por documento no `PrestadorCorpoSocietarioSort`. O cabeçalho diz
            // "Documento / Tipo", então o agrupamento por tipo é o resultado esperado.
            documento: 'TIPO',
            ufSocio: 'UF',
            nome: 'NOME',
            cargo: 'CARGO',
            dataEntrada: 'ENTRADA',
            dataSaida: 'SAIDA',
            email: 'EMAIL'
        };
        return field ? (sortMap[field] ?? null) : null;
    }

    private formatarData(data?: string | null): string {
        return data ? DateUtils.formatDate(data) : '-';
    }

    private requestSalvar(idPrestador: number, ufPrestador: string): PrestadorCorpoSocietarioSocioSalvarRequest {
        const value = this.formSocio.getRawValue();
        return {
            idPrestador,
            ufPrestador,
            idSocio: value.idSocio,
            ufSocio: value.ufSocio,
            tipoSocioCodigo: value.tipoSocioCodigo,
            idCargo: value.idCargo!,
            dataEntrada: value.dataEntrada,
            dataSaida: value.dataSaida || null
        };
    }

    private requestAtualizar(request: PrestadorCorpoSocietarioSocioSalvarRequest, socioOriginal: PrestadorCorpoSocietarioSocioResponse): PrestadorCorpoSocietarioSocioAtualizarRequest {
        return {
            ...request,
            idSocioOriginal: socioOriginal.idSocio,
            ufSocioOriginal: socioOriginal.ufSocio,
            idCargoOriginal: socioOriginal.idCargo!,
            dataEntradaOriginal: socioOriginal.dataEntrada!
        };
    }

    private requestSaidaEmLote(idPrestador: number, ufPrestador: string): PrestadorCorpoSocietarioSaidaEmLoteRequest {
        return {
            idPrestador,
            ufPrestador,
            dataSaida: this.formSaidaEmLote.controls.dataSaida.value,
            itens: this.selecionadosLista().map((socio) => ({
                idSocio: socio.idSocio,
                ufSocio: socio.ufSocio,
                idCargo: socio.idCargo!,
                dataEntrada: socio.dataEntrada!
            }))
        };
    }

    private socioKey(socio: PrestadorCorpoSocietarioSocioResponse): string {
        return `${socio.ufSocio}-${socio.idSocio}-${socio.idCargo ?? ''}-${socio.dataEntrada ?? ''}`;
    }

    private limparSelecionados(): void {
        this.selecionados.set({});
    }

    private validarDatasFormulario(): boolean {
        const dataSaida = this.formSocio.controls.dataSaida;
        const errors = { ...(dataSaida.errors ?? {}) };
        if (this.datasInvalidas()) {
            dataSaida.setErrors({ ...errors, dataSaidaMenorEntrada: true });
            return false;
        }
        delete errors['dataSaidaMenorEntrada'];
        dataSaida.setErrors(Object.keys(errors).length ? errors : null);
        return true;
    }

    private tipoSocioCodigo(tipo?: string | null): number | null {
        const normalized = tipo?.toUpperCase();
        if (normalized === 'MEDICO') return 1;
        if (normalized === 'PRESTADOR') return 5;
        if (normalized === 'OUTROSPF') return 6;
        if (normalized === 'OUTROSPJ') return 4;
        return null;
    }
}
